/**
 * Section 5: 4 synced history charts (OI Score, Underlying Price, PCR, Max
 * Pain) — all four come from ONE joined series (`GET
 * /options-dashboard/{symbol}/history`), rendered as 4 small multiples that
 * share the same `captured_at` x-axis labels. No `chartjs-plugin-zoom`
 * dependency exists in this codebase (checked `package.json`), so
 * "zoom"/date filtering is done via the From/To date inputs re-fetching a
 * narrower range, rather than adding a new library.
 */
import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Grid, Paper, Stack, TextField, Typography } from "@mui/material";
import TimelineIcon from "@mui/icons-material/Timeline";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import optionsDashboardService from "@/services/optionsDashboardService";
import type { HistoryPoint } from "@/types";
import { extractErrorMessage } from "@/utils/errors";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend);

const MAX_PAGES = 10; // safety cap: up to 10 * 200 = 2000 points per range

function formatLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

const chartOptions = {
  responsive: true,
  plugins: { legend: { display: false } },
  scales: { x: { display: false } },
  elements: { point: { radius: 0 } },
};

export function HistoryCharts({ symbol, expiryDate }: { symbol: string; expiryDate?: string }) {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [points, setPoints] = useState<HistoryPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const collected: HistoryPoint[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await optionsDashboardService.getHistory(symbol, {
          from_date: fromDate,
          to_date: toDate,
          expiry_date: expiryDate || undefined,
          page,
          page_size: 200,
        });
        collected.push(...response.items);
        totalPages = response.meta.total_pages;
        page += 1;
      } while (page <= totalPages && page <= MAX_PAGES);
      setPoints(collected);
    } catch (err) {
      setError(extractErrorMessage(err));
      setPoints([]);
    } finally {
      setLoading(false);
    }
  }, [symbol, fromDate, toDate, expiryDate]);

  useEffect(() => {
    load();
  }, [load]);

  const labels = points.map((p) => formatLabel(p.captured_at));

  const series = [
    {
      key: "score",
      title: "OI Score",
      color: "#0F3D63",
      data: points.map((p) => (p.score_v2 !== null && p.score_v2 !== undefined ? Number(p.score_v2) : null)),
    },
    {
      key: "price",
      title: "Underlying Price",
      color: "#00B386",
      data: points.map((p) => (p.spot_price !== null && p.spot_price !== undefined ? Number(p.spot_price) : null)),
    },
    {
      key: "pcr",
      title: "PCR",
      color: "#1976D2",
      data: points.map((p) => (p.local_pcr !== null && p.local_pcr !== undefined ? Number(p.local_pcr) : null)),
    },
    {
      key: "maxpain",
      title: "Max Pain",
      color: "#FB8C00",
      data: points.map((p) => (p.max_pain_strike !== null && p.max_pain_strike !== undefined ? Number(p.max_pain_strike) : null)),
    },
  ];

  return (
    <Box>
      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" alignItems="center">
        <TextField
          label="From date"
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To date"
          type="date"
          size="small"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="outlined" size="small" onClick={load} disabled={loading}>
          Apply Range
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : points.length === 0 ? (
        <Alert severity="info">No data yet — trigger a cycle or wait for the next scheduled poll.</Alert>
      ) : (
        <Grid container spacing={2} alignItems="stretch">
          {series.map((s) => (
            <Grid item xs={12} md={6} key={s.key}>
              <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                  <TimelineIcon fontSize="small" sx={{ color: s.color }} />
                  <Typography variant="subtitle2" fontWeight={700}>
                    {s.title}
                  </Typography>
                </Stack>
                <Box sx={{ height: 220 }}>
                  <Line
                    data={{
                      labels,
                      datasets: [{ label: s.title, data: s.data, borderColor: s.color, backgroundColor: s.color, tension: 0.15 }],
                    }}
                    options={{ ...chartOptions, maintainAspectRatio: false }}
                  />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export default HistoryCharts;
